import React from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { injectIntl } from 'react-intl';
import { Elements } from 'react-stripe-elements';
import { find as _find } from 'lodash';
import axios from 'axios';
import { Button, Row, Col } from 'react-bootstrap';

import { formStatusDispatchHelper, formReset } from 'actions/FormStatusActions';
import { resetCheckout, initCheckout } from 'actions/CheckoutActions';

import { userDataForCheckout } from 'helpers/clients';
import { getSubmitData } from 'helpers/checkout';

import FormErrors from 'components/forms/FormErrors';
import DatePicker from 'components/forms/DatePicker';

import Loader from 'components/layout/Loader';
import CheckoutProceedButton from 'components/ePayment/CheckoutProceedButton';

import Entity from 'components/Entity';
import Data from 'components/Data';

import { entityMetaMapping, PAYMENT_BILL } from 'js/constants';

import { rawClass as ConvertQuoteToInvoiceForm } from './ConvertQuoteToInvoiceForm';

class PreviewQuoteToInvoiceForm extends ConvertQuoteToInvoiceForm {
  constructor(props) {
    super(props);

    this.state = {
      ...this.state,
      previewHTML: '',
      isRendering: false,
    };
  }

  componentDidMount = () => {
    const { show } = this.props;

    if (show) {
      this.onShow();
    }
  };

  componentDidUpdate = (prevProps, prevState) => {
    const { meta, show, formStatus, onSubmitParent } = this.props;
    const { entityLoaded } = this.state;

    if (entityLoaded && !prevState.entityLoaded) {
      this.initiatePreview();
    }

    if (
      meta[entityMetaMapping.invoice] !== undefined &&
      !meta[entityMetaMapping.invoice].pending &&
      !this.entity.getMetaLoaded()
    ) {
      this.entity.setMeta(meta, entityMetaMapping.invoice);
      if (this.entity.getDataLoaded()) {
        this.setState({ entityLoaded: true, entity: this.entity });
      }
    }

    if (show && !prevProps.show) {
      this.onShow();
    }

    if (!show && prevProps.show) {
      this.onExit();
    }

    if (prevProps.formStatus.pending && !formStatus.pending && !formStatus.hasError && onSubmitParent) {
      onSubmitParent(formStatus.response);
    }
  };

  initiatePreview = () => {
    const { formStatus } = this.props;

    // prepare conversion data
    if (!formStatus.pending) {
      this.formValidate().then(data => {
        this.setState({ isRendering: true, previewHTML: '&hellip;' });
        const { quote, checkout } = this.props;
        const submitData = getSubmitData(data, checkout);
        const quoteId = quote.id || quote.getId();
        axios
          .post(`/api/quotes/${quoteId}/previewInvoice.html`, submitData)
          .then(({ data }) => {
            this.setState({ previewHTML: data, isRendering: false });
          })
          .catch(console.error);
      });
    }
  };

  formValidate = () => {
    return new Promise((resolve, reject) =>
      this.setState(
        prevState => ({ entity: prevState.entity.validate() }),
        () => {
          const { entity, owner } = this.state;
          if (entity.isValid) {
            resolve({ ...entity.processedData, owner });
          } else {
            reject();
          }
        }
      )
    );
  };

  doSubmit = data => {
    const { quote, onSubmit } = this.props;
    onSubmit(quote.id || quote.getId(), data);
  };

  onSubmit = e => {
    e.preventDefault();
    e.stopPropagation();

    return false;
  };

  onShow = () => {
    const { appIntl, quote, projectConfig, meta, doInitCheckout } = this.props;

    const docTemplateSettings = _find(projectConfig.data.default_invoice_template_settings, [
      'language',
      appIntl.locale,
    ]);

    this.entity.setData({
      number_prefix: docTemplateSettings ? docTemplateSettings.doc_template.number_prefix : null,
      lead: quote.lead || quote.getLead(),
      language: quote.language || quote.getLanguage(),
      target: quote.target || quote.getTarget(),
      target_organisation: quote.target_organisation || (quote.getTargetOrganisation && quote.getTargetOrganisation()) || null,
      positions: quote.positions || quote.getPositions(),
      currency: quote.currency || quote.getCurrency(),
      vat_name: quote.vat_name || (quote.getVatName && quote.getVatName()) || 'Tax',
      doc_template: docTemplateSettings ? docTemplateSettings.doc_template : null,
      delivery_date: quote.delivery_date || (quote.getDeliveryDate && quote.getDeliveryDate()) || null,
      comment: quote.comment || (quote.getComment && quote.getComment()) || null,
      is_auto_generated: false,
      is_authorized: false,
      deposit: quote.deposit || (quote.getDeposit && quote.getDeposit()) || 0,
      deposit_type: quote.deposit_type || (quote.getDepositType && quote.getDepositType()) || 'percent',
    });

    if (meta[entityMetaMapping.invoice] !== undefined && !meta[entityMetaMapping.invoice].pending) {
      this.entity.setMeta(meta, entityMetaMapping.invoice);

      this.setState({
        entityLoaded: true,
        entity: this.entity,
      });
    }

    doInitCheckout(quote.positions || quote.getPositions(), userDataForCheckout(quote.target || quote.getTarget()), {
      activePaymentPlugin: PAYMENT_BILL,
      payment: 'bill',
      navToCheckout: false,
    });
  };

  onExit = () => {
    this.entity = new Entity();
    this.setState({ entity: null, entityLoaded: false, entityChanged: false, editedRow: null, showAddItem: false });
  };

  getDateForPicker = () => {
    const { entity } = this.state;
    const D = entity.getCdate() ? new Date(entity.getCdate()) : new Date();
    D.setHours(12);
    D.setMinutes(0);
    D.setSeconds(0);
    return D;
  };

  render() {
    const {
      intl: { messages },
      appIntl,
      formStatus,
    } = this.props;

    const { entityLoaded, editedRow, previewHTML, isRendering } = this.state;

    return (
      <Elements locale={appIntl.locale} key={appIntl.locale}>
        <>
          <Data dataName="invoice" />
          <FormErrors formStatus={formStatus} />

          {entityLoaded && previewHTML.length > 0 ? (
            <>
              <Row>
                <Col xs={4} md={2}>
                  <DatePicker
                    name="cdate"
                    label={messages.salesDoc.invoice_date}
                    onChange={this.updateEntity}
                    value={this.getDateForPicker()}
                    locale={appIntl.locale}
                    disabled={isRendering}
                  />
                </Col>
                <Col xs={4} md={2}>
                  <Button onClick={this.initiatePreview} disabled={isRendering} style={{ marginTop: '20px' }}>
                    <i className="fas fa-sync-alt" />
                    Vorschau aktualisieren
                  </Button>
                </Col>
              </Row>

              <h5>{messages.invoice_preview}</h5>

              <iframe
                style={{
                  width: '100%',
                  minHeight: '30em',
                  height: '50vh',
                  border: '1px solid #CCC',
                }}
                title="Invoice preview"
                srcDoc={previewHTML}
                name="preview"
                sandbox="allow-same-origin"
              />
              <div className="text-right m-t-50">
                <CheckoutProceedButton
                  bsSize="lg"
                  bsStyle="primary"
                  disabled={formStatus.pending || editedRow !== null}
                  label={messages.convert_to_invoice}
                  validate={this.formValidate} // promise
                  onSubmit={this.doSubmit} // function that will receive the data and should send the data to the backend
                  formStatus={formStatus}
                  raiseError={this.raiseError} // function that will display an error message
                />
              </div>
            </>
          ) : (
            <Loader />
          )}
        </>
      </Elements>
    );
  }
}

const mapStateToProps = state => {
  return {
    webApp: state.webApp,
    appIntl: state.intl,
    meta: state.entityMeta,
    formStatus: state.formStatus.convertQuoteToInvoice,
    user: state.login.user,
    projectConfig: state.data.projectConfig,
    variants: state.list.itemVariants,
    checkout: state.checkout,
  };
};
const mapDispatchToProps = dispatch => {
  return {
    doInitCheckout: (items, userData, initData) => {
      dispatch(resetCheckout(initCheckout(items, userData, initData)));
    },
    getPreview: (quoteId, args) => {
      formStatusDispatchHelper(
        'convertQuoteToInvoice',
        `/api/quotes/${quoteId}/previewInvoice.html`,
        args,
        'put',
        dispatch
      );
    },
    onSubmit: (quoteId, args) => {
      formStatusDispatchHelper(
        'convertQuoteToInvoice',
        `/api/quotes/${quoteId}/convertToInvoice.json`,
        args,
        'put',
        dispatch
      );
    },
    onFormSuccessfull: () => {
      dispatch(formReset('convertQuoteToInvoice'));
    },
  };
};

export default withRouter(
  injectIntl(
    connect(
      mapStateToProps,
      mapDispatchToProps
    )(PreviewQuoteToInvoiceForm)
  )
);
